import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { api } from '../../lib/api';

type Distributor = {
  id: number; name: string; bc_customer_no: string | null;
  email: string | null; phone: string | null; country: string | null;
  is_active: number; created_at: string;
  registration_count: number; company_count: number; claim_count: number;
};

type EditForm = { name: string; country: string; email: string; phone: string; bcCustomerNo: string };
const emptyForm: EditForm = { name: '', country: '', email: '', phone: '', bcCustomerNo: '' };

// Group distributors by country
function groupByCountry(list: Distributor[]) {
  const map = new Map<string, Distributor[]>();
  for (const d of list) {
    const key = d.country ?? '—';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(d);
  }
  return map;
}

export default function AdminDistributors() {
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditForm>(emptyForm);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState<EditForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    api.get<Distributor[]>('/admin/distributors').then(setDistributors).finally(() => setLoading(false));
  }, []);

  const filtered = distributors.filter(d => {
    const q = search.toLowerCase();
    return !q || d.name.toLowerCase().includes(q) ||
      (d.country ?? '').toLowerCase().includes(q) ||
      (d.email ?? '').toLowerCase().includes(q) ||
      (d.bc_customer_no ?? '').toLowerCase().includes(q);
  });

  const grouped = groupByCountry(filtered);
  const countries = [...grouped.keys()].sort((a, b) => a === '—' ? 1 : b === '—' ? -1 : a.localeCompare(b));

  async function saveEdit(id: number) {
    setSaving(true);
    try {
      await api.patch(`/admin/distributors/${id}`, {
        name: editForm.name, country: editForm.country, email: editForm.email,
        phone: editForm.phone, bcCustomerNo: editForm.bcCustomerNo,
      });
      setDistributors(p => p.map(d => d.id === id ? {
        ...d, name: editForm.name, country: editForm.country || null,
        email: editForm.email || null, phone: editForm.phone || null,
        bc_customer_no: editForm.bcCustomerNo || null,
      } : d));
      setEditId(null);
    } finally { setSaving(false); }
  }

  async function toggleActive(d: Distributor) {
    const isActive = !d.is_active;
    await api.patch(`/admin/distributors/${d.id}`, { isActive });
    setDistributors(p => p.map(x => x.id === d.id ? { ...x, is_active: isActive ? 1 : 0 } : x));
  }

  async function deleteDistributor(id: number, name: string) {
    if (!confirm(`Delete "${name}"? Their registrations and companies will be unlinked.`)) return;
    setDeletingId(id);
    try {
      await api.delete(`/admin/distributors/${id}`);
      setDistributors(p => p.filter(d => d.id !== id));
    } finally { setDeletingId(null); }
  }

  async function addDistributor() {
    if (!addForm.name.trim()) return;
    setSaving(true);
    try {
      const d = await api.post<Distributor>('/admin/distributors', {
        name: addForm.name, country: addForm.country || undefined,
        email: addForm.email || undefined, phone: addForm.phone || undefined,
        bcCustomerNo: addForm.bcCustomerNo || undefined,
      });
      setDistributors(p => [...p, { ...d, registration_count: 0, company_count: 0, claim_count: 0 }]);
      setAddForm(emptyForm); setAdding(false);
    } finally { setSaving(false); }
  }

  const totalRegs    = distributors.reduce((s, d) => s + d.registration_count, 0);
  const totalCompanies = distributors.reduce((s, d) => s + d.company_count, 0);

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0b0d0e' }}>Distributors</h1>
          <p className="text-sm mt-0.5" style={{ color: '#8f9b9b' }}>
            {distributors.length} distributors · {countries.filter(c => c !== '—').length} countries · {totalCompanies} companies · {totalRegs} registrations
          </p>
        </div>
        <button onClick={() => { setAdding(a => !a); setAddForm(emptyForm); }}
          style={{ fontSize: 13, padding: '7px 16px', borderRadius: 8, background: '#e00000', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
          + Add distributor
        </button>
      </div>

      {/* Add form */}
      {adding && (
        <div className="rounded-xl p-4 mb-5" style={{ background: '#fff', border: '1px solid #e5e5e5' }}>
          <p className="font-semibold text-sm mb-3" style={{ color: '#0b0d0e' }}>New distributor</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div><label className="label">Name *</label><input className="input" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} placeholder="Distributor name" /></div>
            <div><label className="label">Country</label><input className="input" value={addForm.country} onChange={e => setAddForm(f => ({ ...f, country: e.target.value }))} placeholder="e.g. Netherlands" /></div>
            <div><label className="label">Email</label><input className="input" type="email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} placeholder="contact@distributor.com" /></div>
            <div><label className="label">Phone</label><input className="input" value={addForm.phone} onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 555 000 0000" /></div>
            <div><label className="label">BC Customer No</label><input className="input" value={addForm.bcCustomerNo} onChange={e => setAddForm(f => ({ ...f, bcCustomerNo: e.target.value }))} placeholder="e.g. C00123" /></div>
          </div>
          <div className="flex gap-3 mt-3">
            <button className="btn-primary text-sm" onClick={addDistributor} disabled={saving || !addForm.name.trim()}>
              {saving ? 'Saving…' : 'Add distributor'}
            </button>
            <button onClick={() => setAdding(false)} style={{ fontSize: 14, color: '#8f9b9b', background: 'none', border: 'none', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="mb-4">
        <input className="input" style={{ maxWidth: 320 }} placeholder="Search by name, country, email or BC no…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <p style={{ color: '#8f9b9b', fontSize: 14, padding: '24px 0' }}>Loading…</p>
      ) : (
        <div className="space-y-6">
          {countries.map(country => (
            <div key={country}>
              {/* Country header */}
              <div className="flex items-center gap-3 mb-2">
                <h2 className="font-semibold text-sm" style={{ color: '#0b0d0e' }}>{country}</h2>
                <div style={{ flex: 1, height: 1, background: '#e5e5e5' }} />
                <span style={{ fontSize: 12, color: '#8f9b9b' }}>{grouped.get(country)!.length} distributor{grouped.get(country)!.length !== 1 ? 's' : ''}</span>
              </div>

              <div className="rounded-xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e5e5e5' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', minWidth: 640 }}>
                    <thead style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                      <tr style={{ textAlign: 'left' }}>
                        {['Distributor', 'Contact', 'BC No', 'Companies', 'Regs', 'Claims', 'Status', 'Actions'].map(h => (
                          <th key={h} style={{ padding: '9px 14px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#8f9b9b' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {grouped.get(country)!.map(dist => editId === dist.id ? (
                        <tr key={dist.id} style={{ borderBottom: '1px solid #fafafa', background: '#fffbf5' }}>
                          <td style={{ padding: '10px 14px' }}><input className="input" style={{ fontSize: 13 }} value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} /></td>
                          <td style={{ padding: '10px 14px' }}>
                            <input className="input mb-1" style={{ fontSize: 12 }} placeholder="Email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                            <input className="input" style={{ fontSize: 12 }} placeholder="Phone" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
                          </td>
                          <td style={{ padding: '10px 14px' }}><input className="input" style={{ fontSize: 12, width: 100 }} value={editForm.bcCustomerNo} onChange={e => setEditForm(f => ({ ...f, bcCustomerNo: e.target.value }))} /></td>
                          <td style={{ padding: '10px 14px', color: '#374151' }}>{dist.company_count}</td>
                          <td style={{ padding: '10px 14px', color: '#374151' }}>{dist.registration_count}</td>
                          <td style={{ padding: '10px 14px', color: '#374151' }}>{dist.claim_count}</td>
                          <td style={{ padding: '10px 14px' }} />
                          <td style={{ padding: '10px 14px' }}>
                            <div className="flex gap-3">
                              <button onClick={() => saveEdit(dist.id)} disabled={saving} style={{ fontSize: 12, color: '#16a34a', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Save</button>
                              <button onClick={() => setEditId(null)} style={{ fontSize: 12, color: '#8f9b9b', background: 'none', border: 'none', cursor: 'pointer' }}>Cancel</button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <tr key={dist.id} style={{ borderBottom: '1px solid #fafafa' }}>
                          <td style={{ padding: '12px 14px' }}>
                            <p style={{ fontWeight: 600, color: '#0b0d0e' }}>{dist.name}</p>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            {dist.email && <p style={{ color: '#374151', fontSize: 12 }}>{dist.email}</p>}
                            {dist.phone && <p style={{ color: '#8f9b9b', fontSize: 12 }}>{dist.phone}</p>}
                            {!dist.email && !dist.phone && <span style={{ color: '#d1d5db' }}>—</span>}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#8f9b9b', fontFamily: 'monospace', fontSize: 12 }}>{dist.bc_customer_no ?? '—'}</td>
                          <td style={{ padding: '12px 14px', fontWeight: 500, color: '#374151' }}>{dist.company_count}</td>
                          <td style={{ padding: '12px 14px', fontWeight: 500, color: '#374151' }}>{dist.registration_count}</td>
                          <td style={{ padding: '12px 14px', fontWeight: 500, color: '#374151' }}>{dist.claim_count}</td>
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{ background: dist.is_active ? '#dcfce7' : '#f3f4f6', color: dist.is_active ? '#15803d' : '#6b7280', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999 }}>
                              {dist.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <div className="flex gap-3">
                              <button onClick={() => { setEditId(dist.id); setEditForm({ name: dist.name, country: dist.country ?? '', email: dist.email ?? '', phone: dist.phone ?? '', bcCustomerNo: dist.bc_customer_no ?? '' }); }}
                                style={{ fontSize: 12, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Edit</button>
                              <button onClick={() => toggleActive(dist)}
                                style={{ fontSize: 12, color: dist.is_active ? '#d97706' : '#16a34a', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                {dist.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button onClick={() => deleteDistributor(dist.id, dist.name)} disabled={deletingId === dist.id}
                                style={{ fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                {deletingId === dist.id ? '…' : 'Delete'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p style={{ color: '#8f9b9b', fontSize: 14, textAlign: 'center', padding: '32px 0' }}>No distributors found.</p>
          )}
        </div>
      )}
    </Layout>
  );
}
